import React, { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import * as cardsModule from "../components/card-data";
import Seo from "../components/seo";
import { cardPath } from "../utils/cardRoutes";
import { COMMON_COPY_ES, isSpanishLocale, localizedPath, translateCardName } from "../utils/i18n";

// Normalize card-data export
function getCardsArray(mod) {
  const candidate =
    mod?.default ??
    mod?.cardData ??
    mod?.cards ??
    mod?.data ??
    null;

  if (Array.isArray(candidate)) return candidate;
  if (candidate && typeof candidate === "object") return Object.values(candidate);

  const firstArray = Object.values(mod || {}).find(Array.isArray);
  return Array.isArray(firstArray) ? firstArray : [];
}

// Hook to read query param
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function normalizeSearchText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export default function SearchResults() {
  const isSpanish = isSpanishLocale();
  const query = useQuery().get("query") || "";
  const allCards = useMemo(() => getCardsArray(cardsModule), []);
  const pageTitle = query
    ? `${isSpanish ? COMMON_COPY_ES.searchResultsFor : "Search Results for"} ${query} | TarotFate`
    : isSpanish ? COMMON_COPY_ES.searchEmptyTitle : "Search Tarot Cards | TarotFate";

  const results = useMemo(() => {
    const q = normalizeSearchText(query);
    if (!q) return [];

    return allCards
      .filter((c) => {
        if (!c?.name) return false;
        const searchableNames = [c.name, isSpanish ? translateCardName(c.name) : ""].filter(Boolean);
        return searchableNames.some((name) => normalizeSearchText(name).includes(q));
      })
      .map((c) => ({
        name: c.name,
        slug: cardPath(c),
        localizedSlug: localizedPath(cardPath(c)),
        image: c.image || c.img || c.thumbnail || "",
      }));
  }, [query, allCards, isSpanish]);

  return (
    <div className="min-h-screen px-4 py-8 font-[Cormorant_Garamond] text-white" style={{ background: "linear-gradient(180deg, #014C75 0%, #005C83 34%, #0C4B84 72%, #821E3C 100%)" }}>
      <div className="mx-auto max-w-screen-lg">
      <Seo
        title={pageTitle}
        description={isSpanish ? COMMON_COPY_ES.searchDescription : "Search TarotFate's tarot card library by card name and open complete upright and reversed meanings."}
        path={isSpanish ? "/es/search" : "/search"}
        robots="noindex, follow"
      />
      <h1 className="text-2xl font-semibold mb-4">{isSpanish ? COMMON_COPY_ES.searchResultsFor : "Search Results for"} "{query}"</h1>
      {results.length === 0 ? (
        <p className="text-white/85">{isSpanish ? COMMON_COPY_ES.noResults : "No results found."}</p>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {results.map((item, idx) => (
            <li key={idx} className="overflow-hidden rounded-lg border border-white/15 bg-white/8 shadow-sm">
              <Link to={item.localizedSlug} className="block transition hover:bg-white/10">
                {item.image && (
                  <img
                    src={item.image}
                    alt={translateCardName(item.name)}
                    className="w-full h-40 object-contain bg-white/8"
                  />
                )}
                <div className="p-2 text-center">{translateCardName(item.name)}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      </div>
    </div>
  );
}
